import React from "react";
import {
  ComposedChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from "recharts";

const CONDITION_COLORS = [
  "#3b82f6", "#22c55e", "#f59e0b", "#ef4444", "#8b5cf6", "#ec4899",
];

/**
 * Custom box plot using Recharts ComposedChart.
 * Each "bar" spans Q1–Q3, with whisker lines for min/max and a median marker.
 */
const BoxPlotChart = ({ distributions, conditions, metricLabel, formatValue, height = 220 }) => {
  if (!distributions || conditions.length === 0) {
    return <p className="text-gray-500 text-sm">No data</p>;
  }

  const chartData = conditions.map((c, i) => {
    const d = distributions[c.conditionId];
    if (!d || d.n === 0) {
      return { name: c.label || c.conditionId, colorIndex: i, n: 0, min: 0, q1: 0, median: 0, q3: 0, max: 0, mean: 0, iqr: 0, base: 0 };
    }
    return {
      name: c.label || c.conditionId,
      colorIndex: i,
      n: d.n,
      min: d.min,
      q1: d.q1,
      median: d.median,
      q3: d.q3,
      max: d.max,
      mean: d.mean,
      // For stacked bar: base (invisible) + iqr (visible box)
      base: d.q1,
      iqr: d.q3 - d.q1,
    };
  });

  const allMax = Math.max(...chartData.map((d) => d.max), 1);

  const fmt = formatValue || ((v) => typeof v === "number" ? v.toFixed(1) : v);

  const CustomTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    const d = payload[0]?.payload;
    if (!d || d.n === 0) return null;
    return (
      <div className="bg-gray-800 border border-gray-600 rounded px-3 py-2 text-xs">
        <div className="text-white font-medium mb-1">{d.name}</div>
        <div className="text-gray-300">n = {d.n}</div>
        <div className="text-gray-300">Min: {fmt(d.min)}</div>
        <div className="text-gray-300">Q1: {fmt(d.q1)}</div>
        <div className="text-gray-300">Median: {fmt(d.median)}</div>
        <div className="text-gray-300">Mean: {fmt(d.mean)}</div>
        <div className="text-gray-300">Q3: {fmt(d.q3)}</div>
        <div className="text-gray-300">Max: {fmt(d.max)}</div>
      </div>
    );
  };

  // Custom shape for the box + whiskers
  const BoxWhiskerShape = (props) => {
    const { x, y, width, height: barHeight, payload } = props;
    if (!payload || payload.n === 0) return null;

    const color = CONDITION_COLORS[payload.colorIndex % CONDITION_COLORS.length];
    const yScale = (val) => {
      // Map value to pixel y coordinate within the chart area
      // The bar's y and height correspond to the IQR range
      // We need to compute whisker positions relative to the bar
      if (payload.iqr === 0) return y;
      const pxPerUnit = barHeight / payload.iqr;
      return y + barHeight - (val - payload.q1) * pxPerUnit;
    };

    const centerX = x + width / 2;
    const whiskerWidth = width * 0.4;

    return (
      <g>
        {/* Box (Q1 to Q3) */}
        <rect
          x={x}
          y={y}
          width={width}
          height={Math.max(barHeight, 2)}
          fill={color}
          fillOpacity={0.3}
          stroke={color}
          strokeWidth={1.5}
          rx={2}
        />

        {/* Median line */}
        <line
          x1={x}
          x2={x + width}
          y1={yScale(payload.median)}
          y2={yScale(payload.median)}
          stroke={color}
          strokeWidth={2.5}
        />

        {/* Mean dot */}
        <circle
          cx={centerX}
          cy={yScale(payload.mean)}
          r={3}
          fill="white"
          stroke={color}
          strokeWidth={1}
        />

        {/* Lower whisker (Q1 to min) */}
        <line
          x1={centerX}
          x2={centerX}
          y1={y + barHeight}
          y2={yScale(payload.min)}
          stroke={color}
          strokeWidth={1}
          strokeDasharray="3 2"
        />
        <line
          x1={centerX - whiskerWidth / 2}
          x2={centerX + whiskerWidth / 2}
          y1={yScale(payload.min)}
          y2={yScale(payload.min)}
          stroke={color}
          strokeWidth={1.5}
        />

        {/* Upper whisker (Q3 to max) */}
        <line
          x1={centerX}
          x2={centerX}
          y1={y}
          y2={yScale(payload.max)}
          stroke={color}
          strokeWidth={1}
          strokeDasharray="3 2"
        />
        <line
          x1={centerX - whiskerWidth / 2}
          x2={centerX + whiskerWidth / 2}
          y1={yScale(payload.max)}
          y2={yScale(payload.max)}
          stroke={color}
          strokeWidth={1.5}
        />
      </g>
    );
  };

  return (
    <div>
      {metricLabel && (
        <p className="text-gray-400 text-xs mb-2">{metricLabel}</p>
      )}
      <ResponsiveContainer width="100%" height={height}>
        <ComposedChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
          <XAxis
            dataKey="name"
            tick={{ fill: "#9ca3af", fontSize: 10 }}
            stroke="#4b5563"
            interval={0}
            angle={conditions.length > 4 ? -20 : 0}
            textAnchor={conditions.length > 4 ? "end" : "middle"}
          />
          <YAxis
            domain={[0, Math.ceil(allMax * 1.1)]}
            tick={{ fill: "#9ca3af", fontSize: 11 }}
            stroke="#4b5563"
          />
          <Tooltip content={<CustomTooltip />} />

          {/* Invisible base bar */}
          <Bar dataKey="base" stackId="box" fill="transparent" barSize={36} />

          {/* IQR bar with custom shape */}
          <Bar
            dataKey="iqr"
            stackId="box"
            barSize={36}
            shape={<BoxWhiskerShape />}
          >
            {chartData.map((entry, i) => (
              <Cell key={i} fill="transparent" />
            ))}
          </Bar>
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
};

export default BoxPlotChart;
